import { useFormik } from "formik";
import { NumericFormat } from "react-number-format";
import { parseStringToNumber } from "../../../../../helperFunctions";
import {
  SingleCooperativeDetails,
  SingleMemberContributionDetails,
} from "../../../../../redux/features/cooperative/cooperative.type";
import {
  LoanDisbursementMethod,
  LoanProduct,
  RequestLoanPayload,
} from "../../../../../redux/features/loans/loans.type";
import { useGetMemberBankAccountsQuery } from "../../../../../redux/features/payment/payment.api";
import { loanDisbursementMethodMap } from "../../../../../utils/constants";
import { getLoanRequestInformationSchema } from "../../../../../validationSchema";
import { Button } from "../../../../buttons/button";
import { Icon } from "../../../../icons/icon";
import ComboBox from "../../../../inputs/combobox";
import { TextInput } from "../../../../inputs/textInput";

export const RequestLoanInformation = ({
  cooperativeDetails,
  onSuccess,
  finalPayload,
  setFinalPayload,
  selectedLoanProduct,
  memberContributionsDetails,
}: {
  cooperativeDetails: SingleCooperativeDetails;
  onSuccess: (data: Partial<RequestLoanPayload>) => void;
  finalPayload?: Partial<RequestLoanPayload>;
  setFinalPayload: (data: Partial<RequestLoanPayload>) => void;
  selectedLoanProduct?: LoanProduct;
  memberContributionsDetails: SingleMemberContributionDetails;
}) => {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    handleBlur,
    handleChange,
  } = useFormik({
    initialValues: {
      loanproductID: finalPayload?.loanproductID,
      loanAmount: finalPayload?.loanAmount ?? "",
      disbursemode: finalPayload?.disbursemode ?? "bank",
      disbursebankID: finalPayload?.disbursebankID,
      tenor: finalPayload?.tenor,
    },
    enableReinitialize: true,
    validationSchema: getLoanRequestInformationSchema(
      memberContributionsDetails,
      selectedLoanProduct
    ),
    onSubmit: (data) => {
      const newPayload = {
        ...finalPayload,
        ...data,
        tenor: parseStringToNumber(data.tenor)!,
        loanAmount: parseStringToNumber(data.loanAmount)!,
        disbursebankID:
          data.disbursemode === "bank" ? data.disbursebankID : undefined,
      };

      setFinalPayload(newPayload);

      onSuccess(newPayload);
    },
  });

  const { data: bankAccounts, isFetching: fetchingBankAccounts } =
    useGetMemberBankAccountsQuery(undefined, {
      skip: values.disbursemode !== "bank" || !!finalPayload?.disbursebankID,
    });

  return (
    <form className="flex flex-col gap-4 min-h-full" onSubmit={handleSubmit}>
      <ComboBox
        type="text"
        isDropdown
        name="loanproductID"
        value={selectedLoanProduct?.LoanName}
        menuItems={
          cooperativeDetails?.Settings?.loan?.map((loan) => ({
            name: loan.LoanName,
            value: loan.id,
          })) ?? []
        }
        placeholder="Select loan product"
        onChange={() => {}}
        readOnly
        onMenuClose={() => {}}
        onBlur={handleBlur}
        hasIcon={false}
        errors={
          errors?.loanproductID && touched.loanproductID
            ? errors?.loanproductID
            : ""
        }
        menuClassName="!max-h-[250px] !min-w-[200px] md:!z-[999]"
        position="bottom start"
        onSelectOption={(val: number) => {
          setFieldValue("loanproductID", val);
          setFinalPayload({ loanproductID: val });
        }}
        className=""
        hasTitle
        title="Loan Product"
        containerClassName="!w-full"
        immediate
        loading={false}
      />

      <NumericFormat
        suffix={undefined}
        prefix={undefined}
        decimalScale={2}
        thousandSeparator
        allowLeadingZeros={false}
        customInput={TextInput}
        onBlur={handleBlur}
        onChange={handleChange}
        type="text"
        name={"loanAmount"}
        value={values.loanAmount}
        placeholder="Enter amount"
        errors={
          errors?.loanAmount && touched?.loanAmount ? errors?.loanAmount : ""
        }
        inputClass=""
        className=""
        customIcon={
          <div>
            <Icon svg={"naira"} width={20} height={20} />
          </div>
        }
        customIconPosition="left"
        hasTitle
        title="Loan Amount"
      />

      <ComboBox
        type="text"
        isDropdown
        name="disbursemode"
        value={
          values.disbursemode
            ? loanDisbursementMethodMap[values?.disbursemode].text
            : ""
        }
        menuItems={Object.keys(loanDisbursementMethodMap)
          .map((k) => ({
            name: loanDisbursementMethodMap[k as LoanDisbursementMethod].text,
            value: k,
          }))
          .filter((v) => v.value === "bank")}
        placeholder="Select disbursal method"
        onChange={() => {}}
        readOnly
        onMenuClose={() => {}}
        onBlur={handleBlur}
        hasIcon={false}
        errors={
          errors?.disbursemode && touched.disbursemode
            ? errors?.disbursemode
            : ""
        }
        menuClassName="!max-h-[250px] !min-w-[200px] md:!z-[999]"
        position="bottom start"
        onSelectOption={(val: number) => {
          setFieldValue("disbursemode", val);
        }}
        className=""
        hasTitle
        title="Disbursal Method"
        containerClassName="!w-full"
        immediate
        loading={false}
      />

      <ComboBox
        type="text"
        isDropdown
        name="disbursebankID"
        value={
          values.disbursebankID
            ? `${
                bankAccounts?.data?.find(
                  (acct) => acct.id === values.disbursebankID
                )?.AccountNumber
              } (${
                bankAccounts?.data?.find(
                  (acct) => acct.id === values.disbursebankID
                )?.BankName
              })`
            : ""
        }
        menuItems={
          bankAccounts?.data.map((account) => ({
            name: `${account.AccountNumber} (${account?.BankName})`,
            value: account.id,
          })) ?? []
        }
        placeholder="Select desired bank account"
        onChange={() => {}}
        readOnly
        onMenuClose={() => {}}
        onBlur={handleBlur}
        hasIcon={false}
        errors={
          errors?.disbursebankID && touched.disbursebankID
            ? errors?.disbursebankID
            : ""
        }
        menuClassName="!max-h-[250px] !min-w-[200px] md:!z-[999]"
        position="bottom start"
        onSelectOption={(val: number) => {
          setFieldValue("disbursebankID", val);
        }}
        className=""
        hasTitle
        title="Disbursal Bank Account"
        containerClassName="!w-full"
        immediate
        loading={fetchingBankAccounts}
      />

      <NumericFormat
        suffix={undefined}
        prefix={undefined}
        decimalScale={0}
        thousandSeparator
        allowLeadingZeros={false}
        customInput={TextInput}
        onBlur={handleBlur}
        onChange={handleChange}
        type="text"
        name={"tenor"}
        value={values.tenor}
        placeholder="Enter loan tenor"
        errors={errors?.tenor && touched?.tenor ? errors?.tenor : ""}
        inputClass=""
        className=""
        customIconPosition="right"
        hasTitle
        hasIcon={false}
        title="Loan Tenor/Duration (in months)"
      />

      <div className="pt-4 mt-auto border-t">
        <Button type="submit" className="" text="Proceed" />
      </div>
    </form>
  );
};
